import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { ClientDTO, PagedResultDTOOfParkerDTO, PagedResultDTOOfZoneDTO } from '../../../../../base/models/models.swagger.generated';
import { ClientService } from '../../../../../base/services/client/client.service';
import { ParkerTurnoverReportService } from '../parker-turnover-report.service';
import { Observable } from 'rxjs';
import { IAutocompleteItem } from '../../../../../base/shared/autocomplete/autocomplete-item';
import { map } from 'rxjs/operators';
import { DatePickerComponent, DatePickerModule } from '@syncfusion/ej2-angular-calendars';
import { MultiSelectItem } from '../../../../../base/shared/multi-select-filter/multi-select-item';
import { MultiSelectFilterType } from '../../../../../base/shared/multi-select-filter/multi-select-filter-type';
import { TranslateModule } from '@ngx-translate/core';
import { MultiSelectFilterComponent } from '../../../../../base/shared/multi-select-filter/multi-select-filter.component';
import { AutocompleteComponent } from '../../../../../base/shared/autocomplete/autocomplete.component';
import { ClientAutocompleteComponent } from '../../../../../base/shared/client-autocomplete/client-autocomplete.component';

@Component({
    selector: 'app-parker-turnover-form',
    templateUrl: './parker-turnover-form.component.html',
    styleUrls: ['./parker-turnover-form.component.scss'],
    imports: [
        ReactiveFormsModule,
        ClientAutocompleteComponent,
        DatePickerModule,
        AutocompleteComponent,
        MultiSelectFilterComponent,
        TranslateModule
    ]
})
export class ParkerTurnoverFormComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(ClientAutocompleteComponent)
  public clientAutocomplete: ClientAutocompleteComponent;

  @ViewChild('startTime')
  public startTimePicker: DatePickerComponent;

  @ViewChild('endTime')
  public endTimePicker: DatePickerComponent;

  @Input() public form: UntypedFormGroup;

  @Output()
  public searchDTOChanged = new EventEmitter<any>();

  public get selectedClient(): ClientDTO {
    return this.clientService.selectedClient;
  }

  public set selectedClient(value: ClientDTO) {
    if (this.clientService.selectedClient != value) {
      this.clientService.selectedClient = value;
    }
    // Defer the event emission to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.onClientSelected(value);
    });
  }

  public get selectedZone(): number {
    if (this.form.get('zoneId').value === null) {
      return null;
    }
    return this.form.get('zoneId').value;
  }

  public set selectedZone(value: number) {
    //TODO Currently workaround
    //Check if actual record has been selected
    if (typeof value === 'number') {
      this.form.get('zoneId').setValue(value);
    } else {
      this.form.get('zoneId').setValue(null);
    }
  }

  public get selectedParkerId(): number[] {
    if (this.form.get('parkerId').value === null) {
      return null;
    }
    return this.form.get('parkerId').value;
  }

  public set selectedParkerId(value: number[]) {
    this.form.get('parkerId').setValue(value);
  }

  public MultiSelectFilterType = MultiSelectFilterType;

  private subscriptions = [];

  constructor(protected clientService: ClientService,
              private parkTurnoverReportService: ParkerTurnoverReportService) {
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.form.valueChanges.subscribe(() => {
        if (this.endTimePicker) {
          if (this.form.get('startDate').value) {
            this.endTimePicker.min = this.form.get('startDate').value;
          } else {
            this.endTimePicker.min = null;
          }
        }

        if (this.startTimePicker) {
          if (this.form.get('endDate').value) {
            this.startTimePicker.max = this.form.get('endDate').value;
          } else {
            this.startTimePicker.max = null;
          }
        }

        this.searchDTOChanged.emit();
      }));
  }

  ngAfterViewInit(): void {
    if (!this.selectedClient) {
      this.focusClientAutocomplete();
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  public onClientSelected(client: ClientDTO): void {
    if (client) {
      this.searchDTOChanged.emit();
    }
  }

  public searchZoneAutocompleteValues(searchText: string): Observable<IAutocompleteItem[]> {
    return this.parkTurnoverReportService.searchZones(searchText)
      .pipe(map((zoneAutocompleteValues: PagedResultDTOOfZoneDTO) => {
        const data: IAutocompleteItem[] = [];
        zoneAutocompleteValues.items.forEach(zone => {
          data.push({id: zone.id, name: zone.name});
        });
        return data;
      }));
  }

  public searchParkerDropdownValues(searchText: string): Observable<MultiSelectItem[]> {
    return this.parkTurnoverReportService.searchParkerDropdownValues(searchText)
      .pipe(map((parkerDropdownValues: PagedResultDTOOfParkerDTO) => {
        const data: MultiSelectItem[] = [];
        parkerDropdownValues.items.forEach(customer => {
          const fullName = customer.firstName + ' ' + customer.lastName;
          data.push({id: customer.id, name: fullName});
        });
        return data;
      }));
  }

  private focusClientAutocomplete(): void {
    if (this.clientAutocomplete) {
      this.clientAutocomplete.focus();
    }
  }
}
